import * as React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Calculator,
  Settings,
  LogOut,
  AlertTriangle,
  Shield,
  Sparkles,
  BarChart3,
  Inbox,
} from "lucide-react"

import { useAuthStore } from "../../store/auth.store"
import { useNavigationStore } from "../../store/navigation.store"
import { UnifiedEntitySelector } from "../ui/unified-entity-selector"
import { GlobalDateFilter } from "../ui/global-date-filter"
import { NavMain } from "./NavMain"
import { NavUser } from "./NavUser"
import { toast } from "sonner"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "../ui/sidebar"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip"

// Import centralized URL building functions
import { buildComplianceUrl } from '@/utils/compliance-url-mapping';
import { buildAMLUrl } from '@/utils/aml-url-mapping';
import { buildAccpayUrl } from '@/utils/accpay-url-mapping';
import { useComplianceMenuCounts } from '@/contexts/ComplianceContext';
import { useAMLMenuCounts } from '@/contexts/AMLContext';
import { type MenuIssueType } from '@/utils/compliance-counts';
import { type AMLMenuIssueType } from '@/contexts/AMLContext';
import { usePrepaymentsCountDirect } from '@/hooks/usePrepaymentsCountDirect';

// Function to create navigation structure with compliance, AML, and prepayments counts
const createNavMainItems = (
  selectedClientId: string | null,
  selectedEntityId: string | 'all' | null,
  getMenuCount: (issueType: MenuIssueType) => number,
  getAMLMenuCount: (issueType: AMLMenuIssueType) => number,
  prepaymentsCount: number,
  hasPrepaymentsData: boolean,
  showAllItems: boolean = true,
  userRole?: string,
) => {
  // Define compliance menu items with their issue types (XeroConnectVr-aligned single letters)
  // Types A-N: 14 compliance check types
  const complianceItems = [
    { title: "Issues Summary", issueType: 'all' as MenuIssueType },
    { title: "VAT Issues", issueType: 'A' as MenuIssueType },
    { title: "Mixed VAT", issueType: 'B' as MenuIssueType },
    { title: "Duplicate Bills", issueType: 'C' as MenuIssueType },
    { title: "Spend vs Bill", issueType: 'D' as MenuIssueType },
    { title: "Mixed GL", issueType: 'E' as MenuIssueType },
    { title: "Aged Payables", issueType: 'F' as MenuIssueType },
    { title: "Aged Receivables", issueType: 'G' as MenuIssueType },
    { title: "Unreconciled Bank Transactions", issueType: 'H' as MenuIssueType },
    { title: "Serial Checker", issueType: 'I' as MenuIssueType },
    { title: "Same GL", issueType: 'J' as MenuIssueType },
    { title: "Low Cost Fixed Assets", issueType: 'K' as MenuIssueType },
    { title: "Old Sales Credits", issueType: 'L' as MenuIssueType },
    { title: "Old Purchase Credits", issueType: 'M' as MenuIssueType },
    { title: "Misallocated Items", issueType: 'N' as MenuIssueType },
  ];
  
  // Filter compliance items based on counts and showAllItems setting
  const filteredComplianceItems = showAllItems
    ? complianceItems // Show all items, but will add disabled property below
    : complianceItems.filter(item => getMenuCount(item.issueType) > 0);

  return [
  // INBOX Section - appears at the top
  {
    title: "INBOX",
    url: "/inbox/comments",
    icon: Inbox,
    items: [
      {
        title: "Comments/System",
        url: "/inbox/comments",
      },
      // Only show when entity is selected (not 'all')
      ...(selectedClientId && selectedEntityId && selectedEntityId !== 'all' ? [{
        title: "Email",
        url: `/inbox/emails/${selectedClientId}/${selectedEntityId}`,
      }] : []),
    ],
  },
  // ACCPAY Section
  {
    title: "ACCPAY",
    url: buildAccpayUrl('all', selectedClientId, selectedEntityId),
    icon: Calculator,
    items: [
      {
        title: "Prepayments",
        url: buildAccpayUrl('prepayments', selectedClientId, selectedEntityId),
        count: hasPrepaymentsData ? prepaymentsCount : undefined,
      },
      {
        title: "All Transactions",
        url: buildAccpayUrl('all', selectedClientId, selectedEntityId),
        disabled: false,
      },
    ],
  },
  {
    title: "LEDGER REPAIR",
    url: buildComplianceUrl('all', selectedClientId, selectedEntityId),
    icon: AlertTriangle,
    items: filteredComplianceItems.map(item => {
      const count = getMenuCount(item.issueType);
      return {
        title: item.title,
        url: buildComplianceUrl(item.issueType, selectedClientId, selectedEntityId),
        count: count, // Add count data for badge display
        issueType: item.issueType, // Add issue type for badge identification
        disabled: showAllItems && count === 0, // Disable items with 0 count when showing all
      };
    }),
  },
  // AML section - follows same pattern as Compliance
  (() => {
    // Define AML menu items with their issue types (K-U)
    const amlItems = [
      { title: "All AML Issues", issueType: 'all' as AMLMenuIssueType },
      { title: "New Counterparty", issueType: 'K' as AMLMenuIssueType },
      { title: "Contact Spikes", issueType: 'L' as AMLMenuIssueType },
      { title: "Soft GL Usage", issueType: 'M' as AMLMenuIssueType },
      { title: "Missing Attachments", issueType: 'N' as AMLMenuIssueType },
      { title: "Volume Spikes", issueType: 'O' as AMLMenuIssueType },
      { title: "Structuring", issueType: 'P' as AMLMenuIssueType },
      { title: "Pass-Through", issueType: 'Q' as AMLMenuIssueType },
      { title: "Credit Note Ratio", issueType: 'R' as AMLMenuIssueType },
      { title: "Round Numbers", issueType: 'S' as AMLMenuIssueType },
      { title: "Backdated", issueType: 'T' as AMLMenuIssueType },
      { title: "Shared Bank", issueType: 'U' as AMLMenuIssueType },
    ];

    // Filter AML items based on counts and showAllItems setting (same logic as Compliance)
    const filteredAMLItems = showAllItems
      ? amlItems // Show all items, but will add disabled property below
      : amlItems.filter(item => getAMLMenuCount(item.issueType) > 0);

    return {
      title: "AML DECISIONS",
      url: buildAMLUrl('all', selectedClientId, selectedEntityId),
      icon: Shield,
      items: filteredAMLItems.map(item => {
        const count = getAMLMenuCount(item.issueType);
        return {
          title: item.title,
          url: buildAMLUrl(item.issueType, selectedClientId, selectedEntityId),
          count: count,
          issueType: item.issueType,
          disabled: showAllItems && count === 0,
        };
      }),
    };
  })(),
  // Custom Scan - only visible when entity is selected
  ...(selectedClientId && selectedEntityId && selectedEntityId !== 'all' ? [{
    title: "TOOLS",
    url: `/custom-scan/${selectedClientId}/${selectedEntityId}`,
    icon: Sparkles,
    items: [
      {
        title: "AI Insights",
        url: `/custom-scan/${selectedClientId}/${selectedEntityId}`,
      },
      {
        title: "Inbox Rules",
        url: `/email-forwarding/${selectedClientId}/${selectedEntityId}`,
      },
    ],
  }] : []),
  // Analytics - only visible when entity is selected
  ...(selectedClientId && selectedEntityId && selectedEntityId !== 'all' ? [{
    title: "ANALYTICS",
    url: `/analytics/email-forwarding/${selectedClientId}/${selectedEntityId}`,
    icon: BarChart3,
    items: [
      {
        title: "Inbox Rules",
        url: `/analytics/email-forwarding/${selectedClientId}/${selectedEntityId}`,
      },
    ],
  }] : []),
  {
    title: "Settings",
    url: "/settings",
    icon: Settings,
    items: [
      // Convenience shortcut (opens the existing Entity Settings modal on Entity Management page)
      ...(selectedClientId && selectedEntityId && selectedEntityId !== 'all' ? [{
        title: "Entity",
        url: `action:entity-settings`,
      }] : []),
      {
        title: "Tags",
        url: "/settings/tags",
      },
      {
        title: "Users",
        url: "/settings/users",
      },
      {
        title: "Billing",
        url: "/billing",
      },
      // Platform admin links
      ...(userRole === 'platform_admin' ? [
        {
          title: "Xero Apps",
          url: "/settings/xero-apps",
        },
        {
          title: "Migration",
          url: "/admin/migration",
        },
      ] : []),
    ],
  },
]; // End of nav items array
}; // End of createNavMainItems function

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuthStore()
  const { state, toggleSidebar } = useSidebar()
  const [isLoading, setIsLoading] = React.useState(true)
  const [isHoverVisible, setIsHoverVisible] = React.useState(false)
  const [isMobile, setIsMobile] = React.useState(false)
  
  // Compliance counts from context (graceful degradation if no provider)
  const { getMenuCount, hasData: hasComplianceData } = useComplianceMenuCounts();

  // AML counts from context (graceful degradation if no provider)
  const { getAMLMenuCount, hasData: hasAMLData } = useAMLMenuCounts();

  // Prepayments counts from direct API call (independent like compliance)
  const { currentCount: prepaymentsCount, hasData: hasPrepaymentsData } = usePrepaymentsCountDirect();
  
  // Show/hide zero counts toggle state (localStorage persistence)
  const [showAllComplianceItems, setShowAllComplianceItems] = React.useState(() => {
    try {
      const saved = localStorage.getItem('showZeroComplianceCounts');
      return saved === null ? true : JSON.parse(saved); // Default to true (show all)
    } catch {
      return true; // Fallback if localStorage fails
    }
  });
  
  // Update localStorage when toggle changes
  React.useEffect(() => {
    try {
      localStorage.setItem('showZeroComplianceCounts', JSON.stringify(showAllComplianceItems));
    } catch (error) {
      console.warn('Failed to save compliance toggle preference to localStorage:', error);
    }
  }, [showAllComplianceItems]);
  
  // Entity selection state
  const {
    clients,
    entities,
    selectedClientId,
    selectedEntityId,
    isLoadingClients,
    isLoadingEntities,
    loadClients,
    setClientId,
    setEntityId,
  } = useNavigationStore()
  
  
  // Load clients on mount (legacy - now using direct API for prepayments counts)
  React.useEffect(() => {
    loadClients(true).catch(error => {
      console.error('[AppSidebar] loadClients failed:', error);
    });
  }, [loadClients])
  
  // Check if device is mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || 'ontouchstart' in window)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  // Simulate loading state for smoother transitions
  React.useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 150)
    return () => clearTimeout(timer)
  }, [])
  
  // Hover timeout ref
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  
  // Debounced hover handlers for smoother interaction
  // 200ms delay prevents accidental triggers when clicking near the left edge (e.g. table checkboxes)
  const handleHoverEnter = React.useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHoverVisible(true)
      hoverTimeoutRef.current = null
    }, 200)
  }, [])
  
  const handleHoverLeave = React.useCallback((e?: React.MouseEvent) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null // Explicitly null the ref to prevent orphaned timeouts
    }
    // Don't hide if moving to dropdown menu
    if (e && e.relatedTarget) {
      const target = e.relatedTarget as Element
      // Check if target is an Element with closest method (not a text node or other non-element)
      if (target && typeof target.closest === 'function' && target.closest('[data-radix-popper-content-wrapper]')) {
        return
      }
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHoverVisible(false)
      hoverTimeoutRef.current = null // Clean up after execution
    }, 300) // 300ms delay before hiding
  }, [])
  
  // Comprehensive cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
        hoverTimeoutRef.current = null
      }
    }
  }, [])

  const handleSignOut = async () => {
    try {
      await signOut()
      toast.success('Logged out successfully')
      navigate('/login')
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to log out'
      toast.error(errorMessage)
    }
  }

  // Handle navigation without auto-collapse
  const handleNavigation = (path: string) => {
    navigate(path)
    // Don't auto-collapse sidebar on navigation for better UX
    // User can manually collapse if needed
  }
  
  // Handle entity selection changes
  const handleEntitySelectionChange = React.useCallback((clientId: string | null, entityId: string | 'all' | null) => {
    if (clientId !== selectedClientId) {
      setClientId(clientId)
    }
    if (entityId !== selectedEntityId) {
      setEntityId(entityId, location.pathname)

      // Fix URL/route parameter mismatch by updating URL when entity changes
      if (clientId && entityId && entityId !== 'all') {
        // Input validation for security
        const isValidId = (id: string) => /^[a-zA-Z0-9-_]{1,50}$/.test(id);
        if (!isValidId(clientId) || !isValidId(entityId)) {
          console.error('Security validation failed: Invalid client or entity ID detected', { clientId, entityId });
          toast.error('Invalid entity selection. Please try again.');
          return;
        }

        // URL encode IDs for defense in depth
        const encodedClientId = encodeURIComponent(clientId);
        const encodedEntityId = encodeURIComponent(entityId);

        const pathSegments = location.pathname.split('/').filter(Boolean);
        const currentPath = location.pathname;

        // Pattern 1: Compliance URLs (highest priority)
        // /compliance/issueType/clientId/entityId
        if (currentPath.match(/^\/compliance\/[^/]+\/[^/]+\/[^/]+$/)) {
          const issueType = pathSegments[1] || 'all';
          navigate(`/compliance/${issueType}/${encodedClientId}/${encodedEntityId}`);
        }
        // Pattern 2: Entity management URLs
        // /clients/clientId/entities or /clients/clientId/entities/entityId
        else if (currentPath.match(/^\/clients\/[^/]+\/entities/)) {
          navigate(`/clients/${encodedClientId}/entities/${encodedEntityId}`);
        }
        // Pattern 3: ACCPAY pages - use path parameters like Compliance/AML
        // /accpay/page/clientId/entityId
        else if (pathSegments[0] === 'accpay' && ['all', 'prepayments'].includes(pathSegments[1])) {
          const page = pathSegments[1] || 'all';
          navigate(`/accpay/${page}/${encodedClientId}/${encodedEntityId}`);
        }
        // Pattern 4: Tools pages with path parameters (mirror Custom Scan)
        // /custom-scan/clientId/entityId OR /email-forwarding/clientId/entityId
        else if (currentPath.match(/^\/(custom-scan|email-forwarding)\/[^/]+\/[^/]+$/)) {
          const tool = pathSegments[0];
          navigate(`/${tool}/${encodedClientId}/${encodedEntityId}`);
        }
        // Pattern 5: Inbox pages with path parameters
        // /inbox/emails/clientId/entityId
        else if (currentPath.match(/^\/inbox\/emails\/[^/]+\/[^/]+$/)) {
          navigate(`/inbox/emails/${encodedClientId}/${encodedEntityId}`);
        }
        // Pattern 6: Analytics pages with path parameters
        // /analytics/email-forwarding/clientId/entityId
        else if (currentPath.match(/^\/analytics\/[^/]+\/[^/]+\/[^/]+$/)) {
          const analyticsType = pathSegments[1];
          navigate(`/analytics/${analyticsType}/${encodedClientId}/${encodedEntityId}`);
        }
        // Pattern 7: Existing query parameter pages (fallback)
        else if (new URLSearchParams(location.search).has('clientId') || new URLSearchParams(location.search).has('entityId')) {
          const searchParams = new URLSearchParams(location.search);
          searchParams.set('clientId', encodedClientId);
          searchParams.set('entityId', encodedEntityId);
          navigate(`${currentPath}?${searchParams.toString()}`);
        }
      }
    }
  }, [selectedClientId, selectedEntityId, setClientId, setEntityId, location.pathname, navigate])

  // Get current client name for tooltip
  const currentClientName = React.useMemo(() => {
    if (!selectedClientId) return null;
    const client = clients.find(c => c.clientId === selectedClientId);
    return client?.clientName || null;
  }, [selectedClientId, clients])
  
  // Note: "All Entities" option removed per user request

  // Get user initials for avatar
  const getInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
    }
    return user?.email?.[0].toUpperCase() || 'U'
  }

  // Enhanced collapsed state with hover trigger
  if (state === 'collapsed') {
    return (
      <>
        {/* Hover trigger zone - invisible area on left edge (desktop only) */}
        {!isMobile && (
          <div
            className="fixed left-0 top-0 z-40 h-full w-1 bg-transparent hover:w-3 transition-all duration-200"
            onMouseEnter={handleHoverEnter}
            onMouseLeave={handleHoverLeave}
            aria-hidden="true"
          />
        )}
        
        {/* Backdrop overlay (desktop only) */}
        {!isMobile && (
          <div
            className={`fixed inset-0 z-30 bg-black/20 backdrop-blur-sm transition-opacity duration-300 ${
              isHoverVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            onClick={() => setIsHoverVisible(false)}
          />
        )}
        
        {/* Overlay sidebar that appears on hover (desktop only) - z-40 is below z-50 used by Radix portals */}
        {!isMobile && (
          <div
            className={`sidebar-hover-overlay fixed left-0 top-0 z-40 h-full w-[17.6rem] transform transition-all duration-300 ease-out ${
              isHoverVisible ? 'translate-x-0 shadow-2xl' : '-translate-x-full shadow-none'
            }`}
            onMouseEnter={handleHoverEnter}
            onMouseLeave={handleHoverLeave}
          >
          <div className="h-full bg-sidebar border-r border-sidebar-border flex flex-col">
            <SidebarHeader className="border-b border-sidebar-border p-4 flex-shrink-0">
              {/* Logo - Clickable to navigate to dashboard */}
              <div className="flex justify-center items-center">
                <button
                  onClick={() => handleNavigation('/dashboard')}
                  className="h-12 w-12 rounded-lg hover:bg-sidebar-accent transition-all duration-200 flex items-center justify-center group hover:scale-105 active:scale-95"
                  title="Go to Dashboard"
                >
                  {isLoading ? (
                    <div className="h-10 w-10 rounded animate-pulse bg-sidebar-accent/20" />
                  ) : (
                    <img
                      src="/logo.png"
                      alt="DRCR Logo"
                      className="h-10 w-10 object-contain transition-transform duration-200 group-hover:scale-110"
                    />
                  )}
                </button>
              </div>
            </SidebarHeader>

            <SidebarContent className="px-3 py-4 flex-1 overflow-y-scroll">
              {/* Entity Selection & Date Filter */}
              <div className="mb-4 pb-3 border-b border-sidebar-border">
                <div className="space-y-2">
                  {/* Entity Selector with Manage Button */}
                  <div className="flex items-center gap-2">
                    <UnifiedEntitySelector
                      clients={clients}
                      entities={entities}
                      selectedClientId={selectedClientId}
                      selectedEntityId={selectedEntityId}
                      onSelectionChange={handleEntitySelectionChange}
                      isLoading={isLoadingClients || isLoadingEntities}
                      placeholder="Select client & entity..."
                    />
                  </div>
                  <GlobalDateFilter
                    buttonSize="sm"
                    className="w-full"
                    isLoading={isLoadingClients || isLoadingEntities}
                  />
                </div>
              </div>
              
              {/* Main Navigation */}
              <div className="space-y-2">
                <NavMain
                  items={createNavMainItems(selectedClientId, selectedEntityId, getMenuCount, getAMLMenuCount, prepaymentsCount, hasPrepaymentsData, showAllComplianceItems, user?.role)}
                  onComplianceToggle={setShowAllComplianceItems}
                  showAllComplianceItems={showAllComplianceItems}
                  disableTooltips={true}
                />
              </div>
            </SidebarContent>

            <SidebarFooter className="border-t border-sidebar-border p-3 flex-shrink-0 mt-auto">
              {/* User Profile */}
              <NavUser user={{
                name: user?.displayName || 'User',
                email: user?.email || 'user@example.com',
                avatar: '', // No avatar for now
              }} />
            </SidebarFooter>
          </div>
          </div>
        )}
      </>
    )
  }

  return (
    <Sidebar 
      collapsible="none" 
      className="shadow-lg md:shadow-none border-r border-sidebar-border" 
      {...props}
    >
      <SidebarHeader className="border-b border-sidebar-border p-4">
        {/* Logo - Clickable to navigate to dashboard */}
        <div className="flex justify-center items-center">
          <button
            onClick={() => handleNavigation('/dashboard')}
            className="h-12 w-12 rounded-lg hover:bg-sidebar-accent transition-all duration-200 flex items-center justify-center group hover:scale-105 active:scale-95"
            title="Go to Dashboard"
          >
            {isLoading ? (
              <div className="h-10 w-10 rounded animate-pulse bg-sidebar-accent/20" />
            ) : (
              <img
                src="/logo.png"
                alt="DRCR Logo"
                className="h-10 w-10 object-contain transition-transform duration-200 group-hover:scale-110"
              />
            )}
          </button>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-3 py-4 flex-1 overflow-y-scroll">
        {/* Entity Selection & Date Filter */}
        <div className="mb-4 pb-3 border-b border-sidebar-border">
          <div className="space-y-2">
            {/* Entity Selector */}
            <div className="flex items-center gap-2">
              <UnifiedEntitySelector
                clients={clients}
                entities={entities}
                selectedClientId={selectedClientId}
                selectedEntityId={selectedEntityId}
                onSelectionChange={handleEntitySelectionChange}
                isLoading={isLoadingClients || isLoadingEntities}
                placeholder="Select client & entity..."
                className="flex-1"
              />
            </div>
            <GlobalDateFilter
              buttonSize="sm"
              className="w-full"
              isLoading={isLoadingClients || isLoadingEntities}
            />
          </div>
        </div>
        
        {/* Main Navigation */}
        <div className="space-y-2">
          <NavMain
            items={createNavMainItems(selectedClientId, selectedEntityId, getMenuCount, getAMLMenuCount, prepaymentsCount, hasPrepaymentsData, showAllComplianceItems, user?.role)}
            onComplianceToggle={setShowAllComplianceItems}
            showAllComplianceItems={showAllComplianceItems}
          />
        </div>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-3 flex-shrink-0">
        {/* User Profile */}
        <NavUser user={{
          name: user?.displayName || 'User',
          email: user?.email || 'user@example.com',
          avatar: '', // No avatar for now
        }} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
